// StepSecurity — step 5 of the wizard. A grid of security-posture cards the
// user can toggle on/off (PII/PHI, Zero Trust, encryption, etc.).
//
// UI-ONLY: selections live in form.securityControls, persist in the local
// draft, but are NOT sent to the backend intake payload (see the "UI-ONLY"
// marker in ../types.ts). Despite the on-screen copy about shaping the
// requirements package, nothing here currently reaches the pipeline.
import type { ComponentType } from 'react'
import {
  Database,
  ShieldCheck,
  Globe,
  Lock,
  KeyRound,
  Search,
  Hexagon,
  Code2,
  ScanLine,
  MapPin,
} from 'lucide-react'

import type { NewProjectForm, UpdateForm } from '../types'

const CONTROLS: Array<{ title: string; meta: string; Icon: ComponentType<{ size?: number; className?: string }> }> = [
  { title: 'PII / PHI Data', meta: 'Sensitive', Icon: Database },
  { title: 'SOC2 / ISO 27001', meta: 'Compliance', Icon: ShieldCheck },
  { title: 'Zero Trust', meta: 'Architecture', Icon: Globe },
  { title: 'Data Encryption', meta: 'At-rest / Transit', Icon: Lock },
  { title: 'SSO / MFA', meta: 'Identity', Icon: KeyRound },
  { title: 'Pen Testing', meta: 'Audit', Icon: Search },
  { title: 'Threat Modeling', meta: 'Design', Icon: Hexagon },
  { title: 'Secret Management', meta: 'Ops', Icon: ScanLine },
  { title: 'Vulnerability Scanning', meta: 'CI/CD', Icon: Code2 },
  { title: 'Data Residency', meta: 'Legal', Icon: MapPin },
]

export function StepSecurity({
  form,
  update,
}: {
  form: NewProjectForm
  update: UpdateForm
}) {
  const toggle = (title: string) => {
    const has = form.securityControls.includes(title)
    update({
      securityControls: has
        ? form.securityControls.filter((c) => c !== title)
        : [...form.securityControls, title],
    })
  }

  return (
    <>
      <header className="step-header">
        <div className="step-section-tag">
          <ShieldCheck size={14} />
          Security Cues
        </div>
        <h1 className="step-h1">What are the <em>security constraints</em>?</h1>
        <p className="step-lede">
          Define the security posture and data-protection requirements so the engineering patterns
          stay compliant. These shape the <span className="accent">requirements package</span>.
        </p>
      </header>

      <div className="archetype-grid">
        {CONTROLS.map(({ title, meta, Icon }) => (
          <button
            key={title}
            type="button"
            className={`archetype-card${form.securityControls.includes(title) ? ' selected' : ''}`}
            onClick={() => toggle(title)}
          >
            <div className="archetype-card-head">
              <Icon size={26} className="archetype-icon" />
              <div className="archetype-check">✓</div>
            </div>
            <div className="archetype-meta">{meta}</div>
            <div className="archetype-title">{title}</div>
          </button>
        ))}
      </div>
    </>
  )
}
